import React, { useState, useEffect, useRef } from 'react';

const ImageMarquee = () => {
  const marqueeRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Images from public directory
  const marqueeImages = [
    "/images/mark5.jpg",
    "/images/mark2.jpg",
    "/images/mark3.jpg",
    "/images/mark4.jpg",
    "/images/mark1.jpg",
 
    "/images/mark6.jpg",
    "/images/mark7.jpg"
  ];

  // Animation speed - reduced for a slower, more elegant scroll
  const scrollSpeed = 0.3;
  
  // Set up the marquee animation using requestAnimationFrame
  useEffect(() => {
    if (!marqueeRef.current) return;
    
    let animationId;
    let position = 0;
    
    // Calculate the width of a single image container
    const imageContainers = marqueeRef.current.querySelectorAll('.image-container');
    if (!imageContainers.length) return;
    
    // Calculate total width of first track
    const trackWidth = marqueeRef.current.querySelector('.marquee-track').offsetWidth;
    
    const animate = () => {
      if (!marqueeRef.current) return;
      
      position -= scrollSpeed;
      
      // Reset position when first track is completely off-screen
      if (position <= -trackWidth) {
        position = 0;
      }
      
      // Apply transform to the container
      marqueeRef.current.style.transform = `translateX(${position}px)`;
      
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);

  // Open image in modal
  const openModal = (imageSrc) => {
    setSelectedImage(imageSrc);
    // Prevent scrolling when modal is open
    document.body.style.overflow = 'hidden';
  };

  // Close modal
  const closeModal = () => {
    setSelectedImage(null);
    // Re-enable scrolling
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="w-full overflow-hidden py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-6 mb-10">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Gallery</h2>
          <div className="h-0.5 w-20 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            Explore our collection of memorable moments and achievements
          </p>
        </div>
      </div>
      
      {/* Outer container with hidden overflow */}
      <div className="relative overflow-hidden mx-4 bg-white">
        {/* Animation container */}
        <div ref={marqueeRef} className="flex whitespace-nowrap">
          {/* First set of images */}
          <div className="marquee-track flex isolate">
            {marqueeImages.map((image, index) => (
              <div 
                key={`track1-${index}`} 
                className="px-4 flex-shrink-0 cursor-pointer transform transition duration-300 hover:scale-105 image-container"
                onClick={() => openModal(image)}
              >
                <div className="relative overflow-hidden rounded-lg group bg-white">
                  <div className="w-80 h-64 overflow-hidden">
                    <img 
                      src={image} 
                      alt={`Gallery image ${index + 1}`} 
                      className="h-64 w-80 object-cover shadow-lg"
                      style={{ isolation: 'isolate' }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              
              </div>
            ))}
          </div>
          
          {/* Duplicate set for seamless looping */}
          <div className="marquee-track flex">
            {marqueeImages.map((image, index) => (
              <div 
                key={`track2-${index}`} 
                className="px-4 flex-shrink-0 cursor-pointer transform transition duration-300 hover:scale-105 image-container"
                onClick={() => openModal(image)}
              >
                <div className="relative overflow-hidden rounded-lg group bg-white">
                  <div className="w-80 h-64 overflow-hidden">
                    <img 
                      src={image} 
                      alt={`Gallery image ${index + 1}`} 
                      className="h-64 w-80 object-cover shadow-lg"
                      style={{ isolation: 'isolate' }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div 
            className="relative max-w-4xl max-h-screen"
            onClick={e => e.stopPropagation()}
          >
            <button 
              onClick={closeModal}
              className="absolute -top-12 right-0 text-white hover:text-blue-300 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              src={selectedImage} 
              alt="Enlarged gallery image" 
              className="max-w-full max-h-screen object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageMarquee;